import { useEffect, useState } from "react";
import { Users, ChevronDown, Download } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { exportLeadsCsv } from "../lib/exportCsv";

const exportRep = (r) => api.get("/leads", { params: { bucket: "potential", potential_of: r.user_id } })
  .then((res) => exportLeadsCsv(res.data, {}, `potential-${r.name.toLowerCase().replace(/[^a-z0-9]+/g, "_")}`).then(() => toast.success(`Exported ${res.data.length} of ${r.name}'s Potential deals`)))
  .catch((e) => toast.error(`Export failed: ${e.message}`));

const Count = ({ value, label, onClick, testid, strong }) => (
  <button onClick={onClick} disabled={!value || !onClick} data-testid={testid}
    className={`flex-1 min-w-[72px] rounded-xl px-3 py-2 text-left transition-colors disabled:cursor-default ${value && onClick ? "hover:bg-blue-50" : ""}`}>
    <span className={`block font-display text-2xl font-extrabold ${strong ? "text-blue-700" : "text-slate-900"} ${value ? "" : "opacity-40"}`}>{value}</span>
    <span className="block text-[10px] sm:text-[11px] uppercase tracking-wider text-slate-500 whitespace-nowrap">{label}</span>
  </button>
);

// Jeff's at-a-glance board: owner emails sent (all time) and leads in each rep's Potential now; tap a rep to open their Potential.
export const RepsBoard = ({ refreshKey, onOpen, viewOnly }) => {
  const [reps, setReps] = useState([]);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/reps/activity").then((r) => setReps(r.data)).catch((e) => console.warn("reps", e)); }, [refreshKey]);
  if (!reps.length) return null;
  if (!open) return (
    <button onClick={() => setOpen(true)} data-testid="reps-open-button"
      className="h-10 px-5 rounded-full bg-white border border-slate-200 text-sm font-extrabold tracking-[0.15em] text-slate-900 flex items-center gap-2 hover:border-blue-400 transition-colors">
      <Users className="h-4 w-4 text-blue-700" />REPS <span className="text-slate-400 font-semibold tracking-normal">({reps.length})</span>
    </button>
  );
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 fade-up" data-testid="reps-board">
      <h2 className="font-display text-sm font-extrabold tracking-[0.2em] text-slate-900 flex items-center gap-2 mb-3"><Users className="h-4 w-4 text-blue-700" />REPS
        <button onClick={() => { setOpen(false); onOpen({ user_id: "all", name: "All reps" }, "potential"); }} data-testid="reps-all-potential"
          className="ml-auto h-8 px-3 rounded-full bg-yellow-300 text-yellow-950 text-xs font-bold tracking-normal hover:bg-yellow-400 transition-colors">All reps' Potential</button>
        <button onClick={() => setOpen(false)} className="ml-3 text-xs font-semibold tracking-normal text-slate-500 hover:text-slate-900 flex items-center gap-1" data-testid="reps-close-button">Hide <ChevronDown className="h-3.5 w-3.5 rotate-180" /></button></h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3">
        {reps.map((r) => (
          <div key={r.id} className="rounded-xl bg-slate-50 border border-slate-100 p-2" data-testid={`rep-row-${r.id}`}>
            <button onClick={() => { setOpen(false); onOpen(r, "potential"); }} disabled={!r.user_id} data-testid={`rep-name-${r.id}`}
              title={r.user_id ? `See ${r.name}'s Potential deals` : "No sign-in for this rep"}
              className="px-3 pt-1 text-sm font-semibold text-slate-800 hover:text-blue-700 hover:underline disabled:no-underline disabled:text-slate-500 transition-colors">{r.name}</button>
            {r.potential > 0 && <button onClick={() => exportRep(r)} title={`Download ${r.name}'s Potential deals (Excel)`} data-testid={`rep-export-${r.id}`}
              className="float-right mr-1 mt-1 p-1 rounded-md text-slate-500 hover:text-blue-700 hover:bg-blue-50 transition-colors"><Download className="h-4 w-4" /></button>}
            <div className="flex">
              <Count value={r.emailed} label="Owner emails" strong onClick={viewOnly ? undefined : () => { setOpen(false); onOpen(r, "rep_emailed"); }} testid={`rep-emailed-${r.id}`} />
              <Count value={r.potential} label="Potential" onClick={() => { setOpen(false); onOpen(r, "potential"); }} testid={`rep-potential-${r.id}`} />
              <Count value={r.signed} label="Signed" onClick={() => { setOpen(false); onOpen(r, "signed"); }} testid={`rep-signed-${r.id}`} />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
